import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/react';
import { useAppUser } from '@/lib/useAppUser';
import { isOnboardingComplete } from '@/lib/onboarding';
import { useDocumentsPending, useAcceptDocument } from '@/lib/documents/useDocuments';
import type { PendingDocument } from '@/lib/documents/types';
import { useDismissedSoftKeys, useDismissSoft, useTermsForceCheckToken } from '@/store/termsGateStore';
import { useAuthModal, useOpenSignup } from '@/store/authModalStore';
import { useSignupInProgress } from '@/store/signupProgressStore';

const noop = () => {};

/**
 * Player consent gate. Mount once in the app shell.
 *
 * - HARD pending docs render a non-dismissable blocking modal (one at a time,
 *   queued) that the user must accept to continue. This is UX; the server-side
 *   ConsentGuard is the real enforcement.
 * - SOFT pending docs render a dismissible banner.
 * - NONE docs are ignored.
 */
export function TermsGate() {
  // Raw session signal (has a token) from the auth library directly — NOT the
  // app-wide `useAppAuth`, whose `isAuthenticated` now means *fully onboarded*.
  // Consent docs and the server's 403 CONSENT_REQUIRED backstop must be able to
  // surface even before onboarding completes, so the gate keys off a real session.
  const { isAuthenticated } = useAuth();
  const user = useAppUser();
  const forceToken = useTermsForceCheckToken();
  // The server (ConsentGuard) has explicitly blocked an action with a 403
  // CONSENT_REQUIRED — the backstop bumped forceToken. That's an authoritative
  // signal: show the gate regardless of onboarding/funnel state.
  const forced = forceToken > 0;
  // Fetch pending docs whenever authenticated so the gate can surface them on
  // load and react instantly to a 403. (Previously this was gated on
  // onboarding-complete, which hid the gate for users the server still blocks.)
  const onboardingComplete = isOnboardingComplete(user);
  const gateActive = isAuthenticated;
  const { data: pending, refetch } = useDocumentsPending(gateActive);
  const accept = useAcceptDocument();
  const dismissedSoftKeys = useDismissedSoftKeys();
  const dismissSoft = useDismissSoft();
  // While an initial signup is underway, consent is collected inside that
  // funnel's terms step (recorded with method 'signup') — NEVER by this gate,
  // even when the server forces a check: the pending docs mid-signup ARE the
  // funnel's docs, so a 403 CONSENT_REQUIRED here means the user attempted a
  // gated action before finishing signup. Resume the funnel instead of popping
  // the gate. We key off the persisted `signupInProgress` flag, not just the
  // (in-memory) modal state, so a refresh mid-signup doesn't briefly surface
  // the gate. Normal logins still gate; this gate is re-acceptance only.
  const signupInProgress = useSignupInProgress();
  const modal = useAuthModal();
  const openSignup = useOpenSignup();
  const inSignupFlow = modal === 'signup' || signupInProgress;

  // The 403 backstop bumps forceToken — refetch pending when it changes.
  useEffect(() => {
    if (gateActive && forceToken > 0) refetch();
  }, [forceToken, gateActive, refetch]);

  // Forced check for a user who hasn't finished signup (mid-funnel OR simply
  // not onboarded yet) → reopen the funnel: its terms step owns the docs.
  // Skip when a funnel sheet is already up — the user is mid-step there.
  useEffect(() => {
    if (!forced) return;
    if (!inSignupFlow && onboardingComplete) return;
    if (modal === 'signup' || modal === 'verifyEmail') return;
    openSignup();
    // Re-run per force bump so a repeat 403 after a dismiss reopens again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceToken]);

  const hardDocs = useMemo(() => (pending ?? []).filter((d) => d.enforcementLevel === 'HARD'), [pending]);
  const softDocs = useMemo(
    () => (pending ?? []).filter((d) => d.enforcementLevel === 'SOFT' && !dismissedSoftKeys.includes(d.key)),
    [pending, dismissedSoftKeys],
  );

  const current = hardDocs[0];

  if (!gateActive) return null;
  // Invariant (ARC-145): a user never sees this gate until the signup flow is
  // finished. Signup-flow docs never render as a gate — forced or not (the
  // effect above resumes the funnel instead). This gate is re-acceptance only.
  if (inSignupFlow || !onboardingComplete) return null;

  if (current) {
    return (
      <HardGate
        key={current.documentVersionId}
        doc={current}
        accepting={accept.isPending}
        onAccept={() => accept.mutate({ key: current.key, documentVersionId: current.documentVersionId, method: 'gate' })}
      />
    );
  }
  if (softDocs.length > 0) {
    const doc = softDocs[0];
    return (
      <SoftBanner
        doc={doc}
        onAccept={() => accept.mutate({ key: doc.key, documentVersionId: doc.documentVersionId, method: 'gate' })}
        onDismiss={() => dismissSoft(doc.key)}
      />
    );
  }
  return null;
}

function SafeHtml({ html }: { html: string }) {
  // Keep inline `style` (Tiptap text-align) and `target`/`rel` on links through
  // sanitisation; the `.doc-prose` rules style the result (see index.css).
  const clean = useMemo(() => DOMPurify.sanitize(html, { ADD_ATTR: ['target', 'rel'] }), [html]);
  return <div className="doc-prose max-w-none" dangerouslySetInnerHTML={{ __html: clean }} />;
}

const SCROLL_END_THRESHOLD_PX = 24;

function HardGate({ doc, accepting, onAccept }: { doc: PendingDocument; accepting: boolean; onAccept: () => void }) {
  // Non-dismissable: onClose is a no-op and Escape/backdrop do nothing meaningful.
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // If the content is short enough that it doesn't overflow, there is nothing
  // to scroll — enable acceptance immediately. A ResizeObserver (an external
  // subscription, not a synchronous effect write) measures on mount and again
  // whenever the rendered document height changes, so short docs are always
  // acceptable without the scroll handler ever firing.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (el.scrollHeight - el.clientHeight <= SCROLL_END_THRESHOLD_PX) setScrolledToEnd(true);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Dialog open onClose={noop} size="md">
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-semibold text-primary">{doc.title}</h2>
          {doc.changeSummary && <p className="mt-1 text-sm text-muted">{doc.changeSummary}</p>}
        </div>
        <div
          ref={scrollRef}
          className="max-h-[50vh] overflow-y-auto rounded-card border border-border p-4"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_END_THRESHOLD_PX) setScrolledToEnd(true);
          }}
        >
          <SafeHtml html={doc.contentHtml} />
        </div>
        <Button onClick={onAccept} disabled={accepting || !scrolledToEnd} className="w-full">
          {accepting ? 'Saving…' : scrolledToEnd ? 'I accept' : 'Scroll to the end to accept'}
        </Button>
      </div>
    </Dialog>
  );
}

function SoftBanner({ doc, onAccept, onDismiss }: { doc: PendingDocument; onAccept: () => void; onDismiss: () => void }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface p-4 shadow-card">
      <div className="mx-auto flex max-w-3xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-primary">{doc.name} updated</p>
          {doc.changeSummary && <p className="text-xs text-muted">{doc.changeSummary}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onDismiss}>
            Later
          </Button>
          <Button onClick={onAccept}>Review &amp; accept</Button>
        </div>
      </div>
    </div>
  );
}
